import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { CustomerCredit } from '@/hooks/useCustomerCredits';
import { formatCurrency } from '@/lib/utils';

const refundSchema = z.object({
  amount: z.number().min(0.01, 'Amount must be greater than 0'),
  method: z.string().min(1, 'Refund method is required'),
  refundDate: z.string().min(1, 'Refund date is required'),
  notes: z.string().optional(),
});

type RefundFormValues = z.infer<typeof refundSchema>;

interface RefundCreditModalProps {
  isOpen: boolean;
  onClose: () => void;
  credit: CustomerCredit | null;
  onRefund: (data: { creditId: string; amount: number; method: string; refundDate: string; notes?: string }) => void;
  isLoading: boolean;
}

const RefundCreditModal: React.FC<RefundCreditModalProps> = ({
  isOpen,
  onClose,
  credit,
  onRefund,
  isLoading,
}) => {
  const form = useForm<RefundFormValues>({
    resolver: zodResolver(refundSchema),
    defaultValues: {
      amount: credit?.remaining_amount || 0,
      method: '',
      refundDate: new Date().toISOString().split('T')[0],
      notes: '',
    },
  });

  React.useEffect(() => {
    if (credit) {
      form.reset({
        amount: credit.remaining_amount,
        method: '',
        refundDate: new Date().toISOString().split('T')[0],
        notes: '',
      });
    }
  }, [credit, form]);

  const handleSubmit = (values: RefundFormValues) => {
    if (!credit) return;
    onRefund({
      creditId: credit.id,
      amount: values.amount,
      method: values.method,
      refundDate: values.refundDate,
      notes: values.notes,
    });
    form.reset();
    onClose();
  };


  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Refund Credit</DialogTitle>
        </DialogHeader>

        {credit && (
          <div className="bg-muted rounded-md p-4 mb-4">
            <p className="text-sm text-muted-foreground">
              Available to refund:{' '}
              <span className="font-semibold text-foreground">{formatCurrency(credit.remaining_amount)}</span>
            </p>
          </div>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Refund Amount *</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      min="0.01"
                      max={credit?.remaining_amount || 0}
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '') field.onChange('');
                        else { const num = parseFloat(val); if (!isNaN(num)) field.onChange(num); }
                      }}
                      onBlur={() => { if ((field.value as any) === '' || field.value === undefined) field.onChange(0); }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="method"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Refund Method *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select method" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="EFT">EFT (Bank Transfer)</SelectItem>
                      <SelectItem value="Cash">Cash</SelectItem>
                      <SelectItem value="Cheque">Cheque</SelectItem>
                      <SelectItem value="Card">Card Refund</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="refundDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Refund Date *</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Optional notes about this refund..." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Processing...' : 'Process Refund'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default RefundCreditModal;
